import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { SIDEBAR_MINIMIZED_WIDTH, SIDEBAR_WIDTH } from 'config'
import { ROUTES } from 'routes'

import {
  SIDEBAR_MENU_ANCHOR_ORIGIN,
  SIDEBAR_MENU_PAPER_SX,
  SIDEBAR_MENU_TRANSFORM_ORIGIN,
} from '../../sidebarMenuPlacement'

import {
  ArrowUpRightIcon,
  BookOpenIcon,
  BugIcon,
  CircleDotIcon,
  CircleHelpIcon,
  CommandIcon,
  LifeBuoyIcon,
  LightbulbIcon,
  MegaphoneIcon,
  CompassIcon,
  SearchIcon,
  TicketIcon,
} from 'lucide-react'

import { Box, ListItem, ListItemButton, ListItemIcon, ListItemText, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { navSelected } from '@repo/ui'

import { openKeyboardShortcutsDialog } from 'components/feedback/KeyboardShortcutsDialog'
import { KeyChip } from 'components/display'
import { ActionDropdown, ActionDropdownItem } from 'components/input/ActionDropdown'
import { openToursDialog } from 'components/onboarding'
import { isDocsPath, useDocsIndexPath } from 'docs/surface'
import { useIsInternalUser, useReferrerState, useShortcutBindings } from 'hooks'
import { useAnnouncementFeed } from 'hooks/api'
import { formatBinding, getShortcutCatalogEntry } from 'utils'

/**
 * The trailing "N new" pill on the What's new row.
 *
 * A COUNT, not a bare dot, and the count is the whole reason this is legible: the row
 * already says "What's new", so a dot next to it only repeats the label. "2 new" says
 * how much is waiting, which is what decides whether to click now or later.
 *
 * Rendered in the `hint` slot, the same slot the Search-help and Keyboard-shortcuts
 * rows put their `KeyChip` in — so the icon column stays flush (§21) and no new prop
 * is added to `ActionDropdownItem`.
 */
const NewMarker = ({ count }: { count: number }) => {
  const theme = useTheme()
  return (
    <Box
      component="span"
      sx={{
        px: 2,
        py: 0.5,
        borderRadius: 1.5,
        fontSize: 12,
        fontFamily: 'Lato',
        lineHeight: 1.4,
        fontWeight: 600,
        color: theme.palette.brand[600],
        bgcolor: alpha(theme.palette.brand[500], 0.12),
      }}
    >
      {count} new
    </Box>
  )
}

/**
 * Trailing mark for the one row in this menu that leaves the app.
 *
 * Every other row navigates in-app; Status opens the external Instatus page in a new
 * tab (`openExternal`, `_blank` + `noopener,noreferrer`). Without a mark the row is
 * indistinguishable from the in-app ones, so the new tab arrives unannounced — the
 * user's back button no longer returns them, which reads as the app having broken.
 *
 * Uses the same `hint` slot as `NewMarker` and the KeyChips, so the icon column stays
 * flush (§21) and `ActionDropdownItem` needs no new prop. `ArrowUpRightIcon` is the
 * repo's established "navigate away" glyph (§21).
 *
 * `text.secondary`, not brand: this is a property of the destination, not a call to
 * action, and it must not compete with the row's own label.
 */
const ExternalMark = () => {
  const theme = useTheme()
  return (
    <Box
      component="span"
      sx={{display: 'inline-flex', color: theme.palette.text.secondary}}
      aria-hidden
    >
      <ArrowUpRightIcon strokeWidth={1} size={16} />
    </Box>
  )
}

/**
 * The label and the destination have to agree, and the LABEL is the fixed point:
 * this platform's users are brand operators, not engineers. "Changelog" is
 * developer register; "What's new" is the word for this audience.
 *
 * VRG-221 moved this row from `https://vera.inc/whats-new` to the IN-APP
 * `ROUTES.WHATS_NEW`. It is not a link-target swap — it is what makes the surface
 * possible at all. An external page cannot know who is reading, so it cannot gate an
 * announcement by permission (§24), cannot hide the backlog that predates a new
 * user's signup, and above all cannot say "you asked for this" by joining the
 * announcement to that brand's own feature requests. It also stopped sending someone
 * out of the product to read about the product. The old external URL additionally had
 * a standing dependency on the marketing site serving that path; it never did, so the
 * row 404'd for its whole life.
 *
 * Status stays external: there is nothing to personalise about an incident page, and
 * it deliberately lives outside our own infrastructure so it survives our outages.
 *
 * TEMPORARY HOST. The destination we want is `https://status.vera.inc`, but a custom
 * domain is gated behind Instatus PRO and `status.vera.inc` has no DNS record at all —
 * so this row was a dead link (it resolved to nothing) for its whole life until
 * 2026-08-13. Pointing at the instatus.com host makes it work TODAY rather than leaving
 * a broken row in the menu waiting on a purchase.
 *
 * NOT `vera.instatus.com` — that slug belongs to an UNRELATED company also called Vera.
 * It returns a real, healthy-looking status page titled "Vera - Status", so a title or
 * `summary.json` name match does NOT establish ownership. Ours is `vera-inc`.
 *
 * This link stays dead until the page is published: while it is in draft, Instatus
 * 307s to instatus.com and `summary.json` returns `{"error":"Not authorized!"}`.
 *
 * TODO(VRG-281): swap to `https://status.vera.inc` once Instatus PRO is bought
 * (planned ~2026-08-27) and DNS is pointed. Both hosts serve the same page, so this is
 * a one-line change with no other coupling.
 */
const STATUS_URL = 'https://vera-inc.instatus.com'

interface Props {
  sidebarOpen?: boolean
  /** Opens the search / Ask overlay. Wired to the real overlay in VRG-227. */
  onOpenSearch?: () => void
}

/**
 * The sidebar's "?" Help entry: a single row that opens a popover.
 *
 * Replaces the old Support accordion (`SidebarNestedList`), which was the
 * sidebar's only disclosure group and was broken three ways — its children were
 * unreachable while the sidebar was collapsed, its `external` links were typed
 * as a string but passed a boolean and never read, and its Live Support row
 * rendered `<Link to={undefined}>`.
 *
 * A popover rather than a nav group because these rows are not all destinations:
 * "Search help" opens an overlay and the three intake rows open a form. A nav
 * accordion can only hold links, which is why the old version needed placeholder
 * routes rendering a bare `<div />`.
 *
 * Row order now matches Vergence's: Search help, Keyboard shortcuts, Product tours,
 * Documentation.
 *
 * It used to lead with Documentation, and the reason was explicitly temporary: the search overlay
 * did not exist yet, so "Search help" opened nothing and leading with it would have given a user
 * who opened Help no working destination. That condition is gone — the overlay ships, and it now
 * searches help, docs AND glossary definitions — so search has reclaimed the top row exactly as
 * that note said it should be reconsidered.
 *
 * Opens on HOVER-WITH-INTENT as well as click (`hoverIntent` on the ActionDropdown).
 *
 * It used to be click-only, and the note here said why: Settings sits directly below
 * this row, so a hover-opened menu containing "Report a bug" would fire on ordinary
 * cursor transit toward it. That objection is answered rather than overruled — the
 * shared `useHoverIntent` requires the pointer to REST 200ms before opening, which a
 * pass-through down the rail (60-120ms across a 48px row) does not satisfy. Click and
 * keyboard activation are unchanged; hover is additive, and is the only half a touch
 * device never sees.
 */
export const SidebarHelpPopover = ({ sidebarOpen, onOpenSearch }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()
  // Where the user is when they open Help, so Documentation can hand the docs page a way
  // back to it (§42).
  //
  // Suppressed when the user is ALREADY inside EITHER docs surface. A back link only earns its
  // place by returning you to an interrupted task somewhere else; within docs the reader's own
  // left nav is the index and always shows where you are, so "← Back to Docs" on a docs page
  // said nothing — and from /docs itself it pointed at the page you were on.
  //
  // This used to read `startsWith(ROUTES.DOCS)`, which recognised only the CUSTOMER site: an
  // internal guide's path starts `/internal/docs`, so the suppression never fired there and
  // the guide's own slug was handed on as the referrer. `isDocsPath` covers both sites.
  const location = useLocation()
  const referrerState = useReferrerState()
  const referrer = isDocsPath(location.pathname) ? undefined : referrerState
  // VRG-227: Help is the one row that lights up while reading either docs site — the
  // main nav's own rows explicitly refuse to match a docs path (see SidebarList's
  // `isSelected`), so without this nothing in the sidebar would show where you are.
  const isDocsActive = isDocsPath(location.pathname)

  // Which docs site this user gets. Staff go to /internal/docs; a brand user (and a staffer
  // inside a brand's space) gets /docs. Before VRG-402 this row always navigated to the
  // CUSTOMER index, which for a staffer is the wrong site — and once the sidebar's Docs row
  // is removed (VRG-401) it would have been their only route to docs at all, leaving the
  // internal runbooks reachable only by typing the URL.
  const docsIndexPath = useDocsIndexPath()

  // Live bindings, so a rebind in Settings -> Controls updates the chips below
  // without a reload. The whole point of the catalog is that no surface owns its
  // own copy of a chord.
  const { search } = useShortcutBindings()

  // VRG-221 — the unseen count drives BOTH the marker on the What's new row and the
  // dot on this closed Help row. Shares the feed's query key with the modal and the
  // /whats-new page, so dismissing anywhere clears it everywhere without a reload.
  //
  // The dot on the CLOSED row is the part that matters: a marker only inside the
  // popover is invisible until you already decided to look, which is the one moment it
  // has nothing left to tell you.
  // VRG-378: staff are support, so one of the three "tell us" rows does not apply
  // to them. Same predicate the rest of the app uses — and note its second clause:
  // a staffer inside a brand's SPACE is acting as that brand's user, and there
  // "Contact support" means the normal thing again, so it comes back.
  const isInternal = useIsInternalUser()

  const { data: feed } = useAnnouncementFeed()
  const unseenCount = feed?.unseenCount ?? 0

  const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer')

  // The `?` binding is hardcoded in the dialog's own handler rather than stored,
  // so it comes from the catalog's fixed entry instead of the bindings object.
  // Still the catalog, still one source — read, never typed out here.
  const shortcutsDialogBinding = getShortcutCatalogEntry('openShortcutsDialog')?.defaultBinding

  // Three groups, dividers between, mirroring Vergence's popover:
  //
  //   1. LEARN — docs, search, tours, shortcuts. "How do I do this?"
  //   2. TELL US — the support intake plus the record of what you have already
  //      told us. All four are the same act (say something to Vera / see what
  //      you said), which is why "My requests" belongs here and not adrift at
  //      the bottom.
  //   3. STATE OF THE PRODUCT — What's new and Status. Both external, both
  //      read-only, both "what is going on with Vera" rather than "with me".
  //
  // Every row carries a leading icon (§21) — a menu with a ragged icon column
  // reads as unfinished.
  const items: ActionDropdownItem[] = [
    /*
      LEARN, ordered by what someone opening Help actually wants first.

      Search leads: a person who opened this menu has a question, and the palette is the shortest
      path to an answer — it now searches help, docs AND glossary definitions, so it is the help
      search, not a generic one. It is arguably redundant with the sidebar's own Search row directly
      above (same modal, same chord), and that was weighed: naming it HERE is what teaches that the
      palette answers help questions at all.

      Then the two keyboard-adjacent affordances, then Documentation last as the "read the manual"
      fallback — the slowest of the four.
    */
    {
      label: 'Search help',
      icon: <SearchIcon strokeWidth={1} />,
      // Same chord the sidebar's Search row advertises, from the same source.
      hint: <KeyChip muted>{formatBinding(search)}</KeyChip>,
      onClick: () => onOpenSearch?.(),
    },
    {
      // Fires the imperative opener rather than holding dialog state here — the
      // dialog is mounted once in Layout, and a menu row should not own the
      // lifecycle of a global surface.
      label: 'Keyboard shortcuts',
      icon: <CommandIcon strokeWidth={1} />,
      hint: shortcutsDialogBinding ? <KeyChip muted>{shortcutsDialogBinding}</KeyChip> : undefined,
      onClick: () => openKeyboardShortcutsDialog(),
    },
    {
      label: 'Product tours',
      icon: <CompassIcon strokeWidth={1} />,
      onClick: () => openToursDialog(),
    },
    {
      // The knowledge base. Route exists and is registered; its content is
      // VRG-225's to fill, so today it lands on that epic's placeholder rather
      // than a dead link.
      label: 'Documentation',
      icon: <BookOpenIcon strokeWidth={1} />,
      // Carries the referrer so the docs page can offer "← Back to <the page you were
      // on>". Opening the docs is the definitive interrupted-task navigation — you were in
      // the middle of something — so it is the entry point that most needs a way back, and
      // it had none.
      onClick: () => navigate(docsIndexPath, { state: referrer }),
    },
    /*
      NO "Contact support" FOR VERA STAFF (VRG-378).

      Staff ARE support. The row opens the intake that files a ticket into the
      queue those same people work, so for them it offers to escalate to
      themselves — there is nobody upstream. It reads as a dead end at best and, at
      worst, as a suggestion that some other team is standing behind them.

      "Report a bug" and "Suggest a feature" stay, and the distinction is the whole
      point of removing only one of the three: those two are not requests for help,
      they are Vera telling Vera something about the product, which a staffer has
      MORE occasion to do than a brand does, not less. That is also why this hides
      the row rather than disabling it (§27): a disabled control exists to say "ask
      someone for access", and there is no access to be granted here — the action
      is simply not a thing staff do.

      Removing the row moves `divider: true` onto "Report a bug" for staff, so the
      group rule stays on the FIRST row of the group either way (ActionDropdown
      emits the <Divider/> before the MenuItem).
    */
    ...(isInternal
      ? []
      : [
          {
            label: 'Contact support',
            icon: <LifeBuoyIcon strokeWidth={1} />,
            onClick: () => navigate(`${ROUTES.SUPPORT_NEW}?type=support`),
            divider: true,
          } as ActionDropdownItem,
        ]),
    {
      label: 'Report a bug',
      icon: <BugIcon strokeWidth={1} />,
      onClick: () => navigate(`${ROUTES.SUPPORT_NEW}?type=bug`),
      divider: isInternal,
    },
    {
      label: 'Suggest a feature',
      icon: <LightbulbIcon strokeWidth={1} />,
      onClick: () => navigate(`${ROUTES.SUPPORT_NEW}?type=feature`),
    },
    /*
      NO "Feature requests" ROW — the customer-facing board is being retired
      (VRG-324, `context/proposals/feature-request-intake-dedup.md`).

      One was added here earlier in VRG-221, because the board had no nav entry at
      all and was reachable only through the ⌘K palette. That fixed the wrong
      problem: the board is a surface you had to visit in order to rank things you
      had already asked for, and almost nobody visits it — which is why intra-company
      voting was thin regardless of company size. The +1 moves to the moment of
      SUBMISSION instead, where the person is already motivated: the intake will
      search your own company's existing requests and offer to add yours to one.

      So the entry is removed rather than kept pointing at a page that is going away.
      "Suggest a feature" above still opens the intake — which is where the dedupe
      will live — and "My requests" below still shows your own, including features.

      The board page and its ⌘K command still exist until the rest of VRG-324 lands.
    */
    {
      label: 'My requests',
      icon: <TicketIcon strokeWidth={1} />,
      // Now a settings section (Personal → Support) rather than a page of its own — VRG-221.
      // Pointed at the real route, not at /support's redirect, so the popover does not cost a
      // navigation to land where it meant to go.
      //
      // Carries the referrer for the same reason Documentation above does — it is one menu,
      // and this was the one row in it that dropped the way back. (Until the Settings header
      // stopped suppressing the link, passing it here would have done nothing.)
      onClick: () => navigate(ROUTES.SETTINGS_PERSONAL_SUPPORT, { state: referrer }),
    },
    {
      // Sits with Status rather than up in the learn group: both are read-only,
      // "what is going on with Vera" rather than "with me". (Status is still
      // external; this one no longer is — see the constant above.)
      label: "What's new",
      icon: <MegaphoneIcon strokeWidth={1} />,
      // The unseen count as a trailing marker, in the same `hint` slot the two rows
      // above use for their key chips — so it needs no new API on ActionDropdownItem
      // and cannot ruin the icon column (§21).
      hint: unseenCount > 0 ? <NewMarker count={unseenCount} /> : undefined,
      onClick: () => navigate(ROUTES.WHATS_NEW),
      divider: true,
    },
    {
      label: 'Status',
      icon: <CircleDotIcon strokeWidth={1} />,
      // Trailing ↗ marks this as the one row that leaves the app (new tab).
      hint: <ExternalMark />,
      onClick: () => openExternal(STATUS_URL),
    },
  ]

  return (
    <>
    <ActionDropdown
      items={items}
      // Placement is shared with the profile chip and the reader menu — see
      // `sidebarMenuPlacement`, which also carries the reasoning.
      anchorOrigin={SIDEBAR_MENU_ANCHOR_ORIGIN}
      transformOrigin={SIDEBAR_MENU_TRANSFORM_ORIGIN}
      menuPaperSx={SIDEBAR_MENU_PAPER_SX}
      // Opens on rest, not on entry — see the docblock above and `useHoverIntent`.
      hoverIntent
      trigger={({ onClick, hoverProps }) => (
        <ListItem
          // Product-tour anchor (working-conventions §42). The tour's closing
          // step points here: clicking the real row opens this popover, which is
          // where "Product tours" lives — so the tour ends on the control that
          // replays it. Sits on the ListItem, not the ListItemButton, so the
          // spotlight rect covers the full row rather than the inner button.
          data-tour="sidebar-help"
          sx={{ pt: 1, pb: 1, pr: 3, pl: 3, textDecoration: 'none' }}
        >
          <ListItemButton
            // The dot is `aria-hidden` (it is decoration), so the count has to reach a
            // screen reader through the label instead — otherwise the marker is
            // sighted-users-only.
            aria-label={unseenCount > 0 ? `Help, ${unseenCount} new updates` : 'Help'}
            disableRipple
            onClick={onClick}
            // On the BUTTON, not the ListItem, so the hover region is exactly the
            // element the menu anchors to (`onClick` reads the same currentTarget).
            {...hoverProps}
            // VRG-227: the only row in the sidebar that highlights on a docs path — see
            // `isDocsActive` above.
            selected={isDocsActive}
            sx={[
              {
                borderRadius: 2,
                pt: 3,
                pb: 3,
                pr: 4,
                // Same `.Mui-selected` treatment as every other nav row (SidebarList), so
                // "active" reads identically whether it's a route match or a docs match.
                '&.Mui-selected': {
                  background: navSelected(theme),
                  ':hover': { background: navSelected(theme) },
                },
              },
              sidebarOpen
                ? { minWidth: SIDEBAR_WIDTH - 24, justifyContent: 'initial' }
                : { maxWidth: SIDEBAR_MINIMIZED_WIDTH - 24 },
            ]}
          >
            <ListItemIcon
              sx={[
                {
                  color: theme.palette.primary[900],
                  minWidth: 0,
                  justifyContent: 'center',
                  // Anchor for the unseen dot below. `position: relative` on the icon
                  // rather than on the row so the dot tracks the GLYPH — the row's
                  // width changes with the sidebar's collapsed state, the icon's does
                  // not, so anchoring to the row would move the dot on collapse.
                  position: 'relative',
                },
                sidebarOpen ? { mr: 0 } : { mr: 'auto' },
              ]}
            >
              <CircleHelpIcon strokeWidth={1} />
              {/* Visible while the popover is CLOSED and in BOTH sidebar states —
                  collapsed included, which is where it matters most, because that is
                  the state with no labels at all. A bare dot here rather than the
                  count: at 6px there is no room for a numeral, and the row's job is
                  only to say "there is something", with the count one click away on
                  the What's new row itself.

                  Deliberately NOT gated on `sidebarOpen`: the earlier draft hid it
                  when collapsed, which meant a user who keeps the sidebar collapsed —
                  the denser, more common setup — would never learn anything shipped. */}
              {unseenCount > 0 && (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -1,
                    right: -1,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: theme.palette.brand[600],
                  }}
                />
              )}
            </ListItemIcon>
            <ListItemText
              disableTypography
              sx={[
                {
                  fontSize: 16,
                  fontFamily: 'Lato',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'center',
                  marginLeft: 3,
                  // Measured, not guessed. Nav rows sit inside a <Link> whose
                  // line-height is 1; this row has none, so it inherited 1.4 →
                  // a 22.4px line box, which with ListItemText's default 4px
                  // vertical margins makes a 30.4px item — taller than the 24px
                  // icon that otherwise sets the row height. The row therefore
                  // grew 48px → 54.39px the moment the label appeared, and since
                  // the whole bottom block is bottom-anchored, everything above
                  // that edge shifted: the "?" icon jumped from y=475.000
                  // (collapsed) to y=471.797 (expanded). Pinning line-height to 1
                  // returns the expanded row to 48px and the icon to y=475.000 —
                  // zero movement. Same defect VRG-224 fixed on the Search row;
                  // this row was missed.
                  lineHeight: 1,
                },
                sidebarOpen ? { opacity: 1, display: 'flex' } : { opacity: 0, display: 'none' },
              ]}
            >
              Help
            </ListItemText>
          </ListItemButton>
        </ListItem>
      )}
    />
    </>
  )
}

